import React from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@tempmee/react';
import { TableCard } from '@tempmee/react-blocks/experimental';

const toColumn = (column) => (typeof column === 'string' ? { label: column } : column);

/**
 * The shared frame for every DS table list: the `TableCard → Table →
 * TableHead(columns)` scaffold with its loading, empty and error states. Only
 * the columns, the row shape, the card's header slots and the copy differ from
 * table to table, so those stay with each table and the frame lives here.
 *
 * The error branch is ours because `TableCard` has no failure state, and a
 * failed request must not borrow the empty-state copy — "nothing here" and
 * "could not load" are different answers, and only one of them means "try
 * again".
 *
 * `title={null}` is the default because a list page's header usually already
 * names the table. TableCard types `title` as required and renders its header
 * Box (`px-4 pt-4 pb-3`) unconditionally, so a null title removes the text but
 * leaves ~28px of empty band above the table. The real fix is making `title`
 * optional in @tempmee/react-blocks so the header Box is skipped when absent.
 *
 * @param {React.ReactNode} [title] - Card title; `null` when the page header
 *   already names the table, a string or a node when it does not.
 * @param {boolean} [outline] - Forwarded to the DS Card so it reads as a card
 *   against a white background.
 * @param {React.ReactNode} [action] - Header control pinned right of the title.
 *   Kept on the error branch too, since a header control (the office tab's
 *   "Apply promo code") does not depend on the list having loaded; a caller
 *   whose action reports on the data passes `null` on error.
 * @param {React.ReactNode} [toolbar] - Full-width row under the header (search
 *   and filters). Kept on the error branch so an admin can change a filter and
 *   try again.
 * @param {Array<string|{label: string, align?: string, className?: string}>} columns -
 *   Header cells, in order: a label, or a label with the cell's alignment and
 *   class (a column width the DS has no prop for).
 * @param {number} rowCount - Number of rows rendered, for the empty state.
 * @param {React.ReactNode} [footer] - Beneath the loaded table (pagination).
 *   Dropped on the error branch: there is no page to step to.
 * @param {React.ReactNode} children - The `<TableRow>` list.
 */
export const TableCardFrame = ({
  title,
  outline,
  action,
  toolbar,
  columns,
  rowCount,
  isPending,
  isError,
  emptyMessage,
  errorMessage,
  footer,
  children,
}) => {
  if (isError) {
    return (
      <TableCard title={title} outline={outline} action={action} toolbar={toolbar}>
        <Typography className="text-error">{errorMessage}</Typography>
      </TableCard>
    );
  }

  return (
    <TableCard
      title={title}
      outline={outline}
      action={action}
      toolbar={toolbar}
      footer={footer}
      loading={isPending}
      empty={!isPending && rowCount === 0}
      emptyMessage={emptyMessage}
    >
      <TableContainer>
        <Table size="medium">
          <TableHead>
            <TableRow>
              {columns.map(toColumn).map(({ label, align, className }) => (
                <TableCell key={label} align={align} className={className}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{children}</TableBody>
        </Table>
      </TableContainer>
    </TableCard>
  );
};

TableCardFrame.propTypes = {
  title: PropTypes.node,
  outline: PropTypes.bool,
  action: PropTypes.node,
  toolbar: PropTypes.node,
  columns: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      align: PropTypes.oneOf(['inherit', 'left', 'center', 'right', 'justify']),
      className: PropTypes.string,
    }),
  ])).isRequired,
  rowCount: PropTypes.number.isRequired,
  isPending: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  emptyMessage: PropTypes.string.isRequired,
  errorMessage: PropTypes.string.isRequired,
  footer: PropTypes.node,
  children: PropTypes.node,
};

TableCardFrame.defaultProps = {
  title: null,
  outline: false,
  action: null,
  toolbar: null,
  footer: null,
  children: null,
};

export default TableCardFrame;
